import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, TrendingUp, AlertTriangle, CheckCircle2, Clock, Loader2, CalendarDays, Download } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  cyan: '#06b6d4',
  emerald: '#10b981',
  amber: '#f59e0b',
  rose: '#f43f5e',
  purple: '#a855f7',
  blue: '#3b82f6',
  slate: '#64748b',
  teal: '#14b8a6'
};

const PIE_COLORS = [COLORS.cyan, COLORS.amber, COLORS.emerald, COLORS.rose, COLORS.purple, COLORS.blue, COLORS.teal];

function KpiCard({ label, value, suffix, icon: Icon, color, testId }) {
  return (
    <div className="bg-slate-900/60 backdrop-blur border border-slate-800/60 rounded-xl p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-slate-400">{label}</span>
        <div className={`p-2 rounded-lg bg-${color}-500/10`}>
          <Icon className={`w-4 h-4 text-${color}-400`} />
        </div>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-3xl font-bold text-white">{value}</span>
        {suffix && <span className="text-sm text-slate-400">{suffix}</span>}
      </div>
    </div>
  );
}

function ChartCard({ title, children, testId }) {
  return (
    <div className="bg-slate-900/60 backdrop-blur border border-slate-800/60 rounded-xl p-5" data-testid={testId}>
      <h3 className="text-sm font-semibold text-slate-300 mb-4">{title}</h3>
      {children}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs text-slate-400 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-sm font-medium" style={{ color: entry.color }}>
          {entry.name}: {entry.value}
        </p>
      ))}
    </div>
  );
};

export default function AnalyticsPage() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Default: last 6 months
  const defaultFrom = new Date();
  defaultFrom.setMonth(defaultFrom.getMonth() - 6);
  const [fromDate, setFromDate] = useState(defaultFrom.toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const res = await fetch(
        `${API_URL}/api/v2/analytics/overview?from_date=${fromDate}T00:00:00&to_date=${toDate}T23:59:59`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        setData(await res.json());
      }
    } catch (e) {
      console.error('Analytics fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const [exporting, setExporting] = useState(false);
  const exportPdf = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const lang = i18n.language === 'en' ? 'en' : 'nb';
      const res = await fetch(
        `${API_URL}/api/v2/analytics/export-pdf?from_date=${fromDate}T00:00:00&to_date=${toDate}T23:59:59&lang=${lang}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `analyse-${fromDate}-${toDate}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success(t('analytics.pdf_exported') || 'PDF eksportert');
      } else {
        toast.error(t('analytics.pdf_error') || 'Kunne ikke eksportere PDF');
      }
    } catch (e) {
      console.error('PDF export error:', e);
      toast.error(t('analytics.pdf_error') || 'Kunne ikke eksportere PDF');
    } finally {
      setExporting(false);
    }
  };

  // Prepare chart data
  const casesTrend = data?.trends?.cases_created || [];
  const tasksTrend = data?.trends?.tasks_completed || [];

  const typeLabels = {
    ruh: t('analytics.types.ruh'),
    deviation: t('analytics.types.deviation'),
    improvement: t('analytics.types.improvement')
  };
  const typeData = Object.entries(data?.distributions?.cases_by_type || {}).map(([k, v]) => ({
    name: typeLabels[k] || k, value: v
  }));

  const sevLabels = {
    low: t('analytics.severity.low'),
    medium: t('analytics.severity.medium'),
    high: t('analytics.severity.high'),
    critical: t('analytics.severity.critical')
  };
  const sevColors = { low: COLORS.emerald, medium: COLORS.amber, high: COLORS.rose, critical: COLORS.purple };
  const sevData = Object.entries(data?.distributions?.cases_by_severity || {}).map(([k, v]) => ({
    name: sevLabels[k] || k, value: v, fill: sevColors[k] || COLORS.slate
  }));

  const statusLabels = {
    not_started: t('analytics.status.not_started'),
    in_progress: t('analytics.status.in_progress'),
    completed: t('analytics.status.completed')
  };
  const statusColors = { not_started: COLORS.slate, in_progress: COLORS.amber, completed: COLORS.emerald };
  const statusData = Object.entries(data?.distributions?.task_status || {}).map(([k, v]) => ({
    name: statusLabels[k] || k, value: v, fill: statusColors[k] || COLORS.slate
  }));

  const sourceLabels = {
    ik_flow: t('analytics.sources.ik_flow'),
    iso_flow: t('analytics.sources.iso_flow'),
    mf_flow: t('analytics.sources.mf_flow'),
    manual: t('analytics.sources.manual'),
    risk: t('analytics.sources.risk')
  };
  const sourceData = Object.entries(data?.distributions?.tasks_by_source || {}).map(([k, v]) => ({
    name: sourceLabels[k] || k, value: v
  }));

  const kpis = data?.kpis || {};

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]" data-testid="analytics-loading">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-cyan-500 animate-spin mx-auto mb-3" />
            <p className="text-slate-400">{t('analytics.loading')}</p>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto space-y-6" data-testid="analytics-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <BarChart3 className="w-7 h-7 text-cyan-500" />
              <h1 className="text-2xl font-bold text-white">{t('analytics.title')}</h1>
            </div>
            <p className="text-slate-400 text-sm">{t('analytics.subtitle')}</p>
          </div>

          {/* Date range picker + Export */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="flex items-center gap-3 bg-slate-900/60 border border-slate-800/60 rounded-xl px-4 py-2.5" data-testid="analytics-date-range">
              <CalendarDays className="w-4 h-4 text-slate-400 hidden sm:block" />
              <div className="flex items-center gap-2 text-sm flex-wrap">
                <label className="text-slate-500">{t('analytics.from')}</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-white text-sm"
                  data-testid="analytics-from-date"
                />
                <label className="text-slate-500">{t('analytics.to')}</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-white text-sm"
                  data-testid="analytics-to-date"
                />
              </div>
            </div>
            <button
              onClick={exportPdf}
              disabled={exporting}
              className="flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors"
              data-testid="analytics-export-pdf-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              PDF
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analytics-kpis">
          <KpiCard label={t('analytics.kpis.total_tasks')} value={kpis.total_tasks} icon={BarChart3} color="cyan" testId="kpi-total-tasks" />
          <KpiCard label={t('analytics.kpis.completion_rate')} value={`${kpis.completion_rate}%`} icon={TrendingUp} color="emerald" testId="kpi-completion-rate" />
          <KpiCard label={t('analytics.kpis.open_cases')} value={kpis.open_cases} icon={AlertTriangle} color="amber" testId="kpi-open-cases" />
          <KpiCard label={t('analytics.kpis.avg_close_days')} value={kpis.avg_close_days} suffix={t('analytics.days')} icon={Clock} color="purple" testId="kpi-avg-close" />
        </div>

        {/* Row 2: More KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard label={t('analytics.kpis.completed_tasks')} value={kpis.completed_tasks} icon={CheckCircle2} color="emerald" testId="kpi-completed-tasks" />
          <KpiCard label={t('analytics.kpis.overdue_tasks')} value={kpis.overdue_tasks} icon={Clock} color="rose" testId="kpi-overdue" />
          <KpiCard label={t('analytics.kpis.total_cases')} value={kpis.total_cases} icon={AlertTriangle} color="cyan" testId="kpi-total-cases" />
          <KpiCard label={t('analytics.kpis.closed_cases')} value={kpis.closed_cases} icon={CheckCircle2} color="emerald" testId="kpi-closed-cases" />
        </div>

        {/* Trend Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Cases trend */}
          <ChartCard title={t('analytics.charts.cases_trend')} testId="chart-cases-trend">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={casesTrend}>
                  <defs>
                    <linearGradient id="colorCases" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.cyan} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={COLORS.cyan} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="count" name={t('analytics.charts.cases_trend')} stroke={COLORS.cyan} fillOpacity={1} fill="url(#colorCases)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          {/* Tasks completed trend */}
          <ChartCard title={t('analytics.charts.tasks_trend')} testId="chart-tasks-trend">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={tasksTrend}>
                  <defs>
                    <linearGradient id="colorTasks" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.emerald} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={COLORS.emerald} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="count" name={t('analytics.charts.tasks_trend')} stroke={COLORS.emerald} fillOpacity={1} fill="url(#colorTasks)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        </div>

        {/* Distribution Charts Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Cases by type */}
          <ChartCard title={t('analytics.charts.cases_by_type')} testId="chart-cases-type">
            <div className="h-56">
              {typeData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={typeData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value">
                      {typeData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12, color: '#94a3b8' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-sm text-slate-500">{t('analytics.no_data')}</div>
              )}
            </div>
          </ChartCard>

          {/* Cases by severity */}
          <ChartCard title={t('analytics.charts.cases_by_severity')} testId="chart-cases-severity">
            <div className="h-56">
              {sevData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sevData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 12 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 12 }} width={70} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" name={t('analytics.charts.cases_by_severity')} radius={[0, 4, 4, 0]}>
                      {sevData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-sm text-slate-500">{t('analytics.no_data')}</div>
              )}
            </div>
          </ChartCard>

          {/* Task status */}
          <ChartCard title={t('analytics.charts.task_status')} testId="chart-task-status">
            <div className="h-56">
              {statusData.some(d => d.value > 0) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={statusData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value">
                      {statusData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12, color: '#94a3b8' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-sm text-slate-500">{t('analytics.no_data')}</div>
              )}
            </div>
          </ChartCard>
        </div>

        {/* Tasks by source */}
        {sourceData.length > 0 && (
          <ChartCard title={t('analytics.charts.tasks_by_source')} testId="chart-tasks-source">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sourceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name={t('analytics.charts.tasks_by_source')} radius={[4, 4, 0, 0]}>
                    {sourceData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        )}
      </div>
    </AppShell>
  );
}
